import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { getInventoryDashboard } from '../../../../api/inventoryCountingApi';

function formatDateInput(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');

  return `${y}-${m}-${d}`;
}

function getCurrentMonthRange() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    from: formatDateInput(from),
    to: formatDateInput(now),
  };
}

function getPreviousMonthRange() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const to = new Date(now.getFullYear(), now.getMonth(), 0);
  return {
    from: formatDateInput(from),
    to: formatDateInput(to),
  };
}

function KpiCard({ title, value, subtitle }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="h4" fontWeight={700} sx={{ mt: 1 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function DashboardCards({ refreshKey }) {
  const defaultRange = useMemo(() => getCurrentMonthRange(), []);

  const [from, setFrom] = useState(defaultRange.from);
  const [to, setTo] = useState(defaultRange.to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadDashboard = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getInventoryDashboard({ from, to });
      setData(result);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          'Nie udało się pobrać dashboardu.',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, [refreshKey, from, to]);

  const setCurrentMonth = () => {
    const range = getCurrentMonthRange();
    setFrom(range.from);
    setTo(range.to);
  };

  const setPreviousMonth = () => {
    const range = getPreviousMonthRange();
    setFrom(range.from);
    setTo(range.to);
  };

  console.log('from:', from, 'to:', to, 'data:', data);
  return (
    <Box sx={{ mb: 3 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="h6">Dashboard</Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button variant="outlined" onClick={setCurrentMonth}>
            Bieżący miesiąc
          </Button>

          <Button variant="outlined" onClick={setPreviousMonth}>
            Poprzedni miesiąc
          </Button>

          <TextField
            size="small"
            label="Od"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            size="small"
            label="Do"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading && !data ? (
        <CircularProgress />
      ) : (
        data && (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <KpiCard
                  title="Produkty z zapasem"
                  value={data.stockProducts}
                  subtitle={`Magazyn: ${data.warehouseCode}`}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <KpiCard
                  title="Niezliczone"
                  value={data.notCounted}
                  subtitle="Brak pełnego liczenia w okresie"
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <KpiCard
                  title="Zliczone min. 1 raz"
                  value={data.countedAtLeastOnce}
                  subtitle={`${data.countedAtLeastOncePercent}%`}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <KpiCard
                  title="Zliczone więcej niż 1 raz"
                  value={data.countedMoreThanOnce}
                  subtitle={`${data.countedMoreThanOncePercent}%`}
                />
              </Grid>
            </Grid>

            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Postęp w wybranym okresie
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {data.countedAtLeastOncePercent}%
                    </Typography>
                  </Stack>

                  <LinearProgress
                    variant="determinate"
                    value={Math.min(data.countedAtLeastOncePercent || 0, 100)}
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                </Stack>
              </CardContent>
            </Card>
          </>
        )
      )}
    </Box>
  );
}
